import { StoryblokComponent } from "@frend-digital/storyblok";
import clsx from "clsx";
import { atom, useAtom, useAtomValue } from "jotai";
import { useEffect } from "react";
import Modal from "@/components/ui/Modal";
import { useGlobalData } from "@/features/GlobalDataContext/GlobalDataContext";
import { useTranslations } from "@/i18n/client";
import { useProductSizeGuide } from "../../SizeGuideProvider";
import styles from "./index.module.css";

type SizeGuide = {
  sizeguide:
    | {
        chart: {
          [measurement: string]: string;
          size: string;
        }[];
        keys: string[];
      }
    | undefined;
};

const SizeChartTable = ({ sizeguide }: SizeGuide) => {
  const unit = useAtomValue(unitAtom);
  const t = useTranslations();

  const convertValue = (value: string): string => {
    const numericValue = parseFloat(value);
    if (isNaN(numericValue)) return value;

    if (unit === "in") {
      return (numericValue / 2.54).toFixed(1);
    }
    return numericValue.toFixed(1);
  };

  if (!sizeguide)
    return (
      <div>
        <p>{t("sizechart.emptysizechart")}</p>
      </div>
    );

  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <th className={styles.heading}>Size</th>
          {sizeguide?.keys?.map((key: string) => (
            <th key={key} className={styles.heading}>
              {key}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sizeguide.chart.map(row => (
          <tr key={row.size} className={styles.row}>
            <td className={styles.td}>{row.size}</td>
            {sizeguide.keys.map((key: string) => (
              <td
                className={styles.td}
                style={{ whiteSpace: "nowrap" }}
                key={key}
              >
                {`${convertValue(row[key])}`}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export const SizeChartModal = ({ sizeguide }: SizeGuide) => {
  const { sizeChartItems } = useProductSizeGuide();
  const { globalData, preview } = useGlobalData();

  const sizeGuideAccordion = globalData?.sizeGuideAccordion?.at(-1);

  useEffect(() => {
    if (sizeChartItems) {
      sizeGuideAccordion?.items?.unshift(...(sizeChartItems ?? []));
    }
  }, [sizeChartItems, sizeGuideAccordion?.items]);

  return (
    <Modal title="Size chart" modal={"sizechart"} className={styles.modal}>
      <UnitToggle />
      <div className={styles.sizechart}>
        <SizeChartTable sizeguide={sizeguide} />
      </div>
      {sizeGuideAccordion && (
        <StoryblokComponent
          firstOpen
          blok={sizeGuideAccordion}
          preview={preview}
          className={styles.accordion}
        />
      )}
    </Modal>
  );
};

const unitAtom = atom<"cm" | "in">("cm");

const UnitToggle = () => {
  const [unit, setUnit] = useAtom(unitAtom);

  return (
    <div className={styles.toggles}>
      <button
        onClick={() => setUnit("cm")}
        className={clsx(styles.toggle, unit === "cm" && styles.selected)}
      >
        CM
      </button>
      <button
        onClick={() => setUnit("in")}
        className={clsx(styles.toggle, unit === "in" && styles.selected)}
      >
        IN
      </button>
    </div>
  );
};
